import React, { useEffect, useRef } from "react";
import type * as LType from "leaflet";
import "proj4";
import { SearchResultItem } from "./types";

interface MapProps {
  items: SearchResultItem[];
}

export const SearchMap: React.FC<MapProps> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<{ map: LType.Map; cluster: LType.MarkerClusterGroup | null; L: typeof LType } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;

    (async () => {
      const leafletModule = await import("leaflet");
      await import("proj4leaflet");
      await import("leaflet.markercluster");
      if (cancelled || !containerRef.current) return;

      const L = (
        (leafletModule as unknown as { default?: typeof LType }).default ?? leafletModule
      ) as typeof LType;

      const projectedL = L as typeof LType & {
        Proj: {
          CRS: new (
            code: string,
            definition: string,
            options: {
              resolutions: number[];
              origin: [number, number];
              bounds: LType.Bounds;
            },
          ) => LType.CRS;
        };
      };
      const crs = new projectedL.Proj.CRS(
        "EPSG:3006",
        "+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
        {
          resolutions: [4096, 2048, 1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1, 0.5],
          origin: [-1200000, 8500000],
          bounds: L.bounds([-1200000, 8500000], [4305696, 2994304]),
        },
      );

      if (!stateRef.current) {
        const map = L.map(containerRef.current, {
          crs,
          minZoom: 0,
          maxZoom: 13,
        }).setView([57.7, 12.0], 8);
        L.tileLayer("/api/map-proxy/{z}/{x}/{y}", {
          attribution: "© Lantmäteriet",
          minZoom: 0,
          maxZoom: 13,
        }).addTo(map);
        stateRef.current = { map, cluster: null, L };
      }

      const { map, L: ll } = stateRef.current;
      if (stateRef.current.cluster) {
        map.removeLayer(stateRef.current.cluster);
      }

      const cluster = ll.markerClusterGroup({ maxClusterRadius: 0 });
      const icon = ll.icon({
        iconUrl: "/icons/visuell-identitet/leaflet/marker-icon.png",
        iconRetinaUrl: "/icons/visuell-identitet/leaflet/marker-icon-2x.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        crossOrigin: false,
      });

      const markers: LType.Marker[] = [];
      items.forEach((item) => {
        const locations = item.locations?.length
          ? item.locations
          : item.latitude && item.longitude
            ? [{ address: "", latitude: item.latitude, longitude: item.longitude }]
            : [];

        locations.forEach((location) => {
          if (!location.latitude || !location.longitude
            || isNaN(parseFloat(location.latitude)) || isNaN(parseFloat(location.longitude))) return;

          const marker = ll.marker(
            [parseFloat(location.latitude), parseFloat(location.longitude)],
            { title: item.title, icon },
          );
          marker.bindPopup(
          `<div><a href="${item.url}"><strong>${item.title}</strong></a>${item.ingress ? `<p>${item.ingress}</p>` : ""}</div>`,
          );
          cluster.addLayer(marker);
          markers.push(marker);
        });
      });

      map.addLayer(cluster);
      stateRef.current.cluster = cluster;

      if (markers.length > 0) {
        const group = ll.featureGroup(cluster.getLayers());
        if (group.getBounds().isValid()) {
          map.fitBounds(group.getBounds(), { padding: [30, 30] });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [items]);

  useEffect(() => {
    return () => {
      stateRef.current?.map?.remove();
      stateRef.current = null;
    };
  }, []);

  return (
    <div
      ref={containerRef}
      id="project-search-map"
      className="project-search-react__map"
    />
  );
};
